import { useConvexAuth } from 'convex/react';
import { useQuery } from '@tanstack/react-query';
import { convexQuery } from '@convex-dev/react-query';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { useEffect } from 'react';
import { useMessageReadScope } from '@/contexts/message-read-scope-context';
import {
  forgetStorageUrl,
  pickMemberStorageUrl,
  readLegacyStorageUrl,
  readStorageUrl,
  writeLegacyStorageUrl,
  writeStorageUrl,
} from '@/hooks/storage-url/storage-url-cache';
import { useSharedStorageUrl } from '@/hooks/storage-url/use-shared-storage-url';
import { useStorageUrlCacheScope } from '@/hooks/storage-url/use-storage-url-cache-scope';
import { useStorageUrlRefresh } from '@/hooks/storage-url/use-storage-url-refresh';

export {
  forgetStorageUrl,
  invalidateStorageUrlCache,
} from '@/hooks/storage-url/storage-url-cache';

export type StorageUrlState = {
  url: string | null;
  status: 'loading' | 'ready' | 'unavailable';
};

/**
 * Hook to convert a storage reference (storageKey, legacy storageId, or an
 * already-resolved URL) to a displayable URL.
 *
 * Behavior (resolver-always for R2 storage keys):
 * - Already-a-URL or data: URI → returned as-is.
 * - Legacy Convex `_storage` id (no '/') → resolved via the historical
 *   `assets.queries.getStorageUrl` path.
 * - Any other storage-key reference → resolved via the authenticated narrow
 *   resolver (`api.storage.resolver.resolveStorageUrl`). The resolver returns a
 *   signed URL for PRIVATE objects and a derived public URL for public /
 *   unregistered ones. If it reports `refreshRequired` (private URL
 *   missing/expired/near-expiry), the hook calls the idempotent
 *   `refreshStorageUrl` mutation to mint a fresh signed URL.
 * - Inside a thread share every key resolves share-first (#4716); the member
 *   resolver only runs for the key the share echoed back.
 *
 * Every access decision is live. The resolver subscription stays open for as
 * long as the consumer is mounted, and Convex re-runs it when the reader's
 * membership, share grants or ACLs change, so a revoked reader stops getting
 * the URL on the next delivery. Remembered URLs (`storage-url-cache`) are only
 * placeholders while that subscription loads, bound to the confirmed identity
 * and workspace on the client, wiped on every identity change, and short-lived
 * for private objects. Only the storage key is sent to the server: the actor
 * is whoever is authenticated (#4747).
 *
 * There is intentionally NO client-side public-URL derivation: a private
 * object's key has the exact same `orgSlug/mediaType/uuid.ext` shape as a public
 * one, so the client cannot tell them apart and must never guess.
 */
export function useStorageUrlState(
  storageIdOrUrl: string | Id<'_storage'> | null | undefined,
  options?: {
    /**
     * @deprecated No-op. The hook is now resolver-always for every storage key,
     * so forcing the resolver is the default and this flag has no effect. Kept
     * only for source compatibility with existing call sites; safe to remove.
     */
    forceResolver?: boolean;
    /**
     * Owning branding document for legacy Convex `_storage` media. The backend
     * verifies both organization membership and that the id belongs to this
     * exact branding before returning a URL.
     */
    brandingId?: string;
  }
): StorageUrlState {
  // `forceResolver` is accepted for backwards compatibility but no longer does
  // anything: the resolver is always used for storage keys (see JSDoc).
  void options?.forceResolver;
  const { isAuthenticated } = useConvexAuth();
  const scope = useStorageUrlCacheScope();
  // Member and legacy resolution wait for a CONFIRMED identity, not just a
  // live socket token. During hydration or an account switch the shared query
  // cache can still hold the previous identity's resolver results; the auth
  // lifecycle purges it before confirming the next identity, so nothing it
  // holds is read or rendered until then.
  const actorConfirmed = isAuthenticated && scope !== null;
  const readScope = useMessageReadScope();
  const shareToken = readScope.mode === 'share' ? readScope.token : null;

  const isAlreadyUrl =
    typeof storageIdOrUrl === 'string' &&
    (storageIdOrUrl.startsWith('http') || storageIdOrUrl.startsWith('data:'));

  const isStorageReference =
    !!storageIdOrUrl && !isAlreadyUrl && typeof storageIdOrUrl === 'string';

  // Legacy Convex `_storage` ids have no '/'; R2 storage keys always do
  // (`org/mediaType/uuid.ext`). Same heuristic `getStorageUrl` uses server-side.
  const isLegacyStorageId =
    isStorageReference && !(storageIdOrUrl as string).includes('/');

  const storageKey =
    isStorageReference && !isLegacyStorageId
      ? (storageIdOrUrl as string)
      : null;

  const shared = useSharedStorageUrl({ shareToken, storageKey });

  // The live, access-checked resolver for every storage-key reference (never
  // derived on the client), for as long as this consumer is mounted.
  const shouldQuery =
    storageKey !== null && actorConfirmed && shared.memberAdmitted;
  const { data } = useQuery({
    ...convexQuery(
      api.storage.resolver.resolveStorageUrl,
      shouldQuery && storageKey ? { storageKey } : 'skip'
    ),
    enabled: shouldQuery,
  });
  const live = shouldQuery ? data : undefined;
  const liveDenied =
    live !== undefined && live.url === null && !live.refreshRequired;

  // Legacy `_storage` id path, confirmed-identity-only and live like the
  // resolver. A share can't admit a legacy Convex id (nothing maps it to the
  // thread), so inside a thread share it fails closed for every viewer (#4716).
  const legacyCacheKey = isLegacyStorageId
    ? `${options?.brandingId ?? 'generic'}|${storageIdOrUrl as string}`
    : null;
  const legacyBlockedByShare = isLegacyStorageId && readScope.mode === 'share';
  const shouldQueryLegacy =
    isLegacyStorageId && !legacyBlockedByShare && actorConfirmed;
  const { data: legacyData } = useQuery({
    ...convexQuery(
      api.assets.queries.getStorageUrl,
      shouldQueryLegacy && storageIdOrUrl
        ? {
            storageId: storageIdOrUrl,
            ...(options?.brandingId ? { brandingId: options.brandingId } : {}),
          }
        : 'skip'
    ),
    enabled: shouldQueryLegacy,
  });
  const liveLegacy = shouldQueryLegacy ? legacyData : undefined;
  const cachedLegacy =
    legacyCacheKey && shouldQueryLegacy
      ? readLegacyStorageUrl(scope, legacyCacheKey)
      : null;

  useEffect(() => {
    if (legacyCacheKey && liveLegacy !== undefined) {
      writeLegacyStorageUrl(scope, legacyCacheKey, liveLegacy);
    }
  }, [scope, legacyCacheKey, liveLegacy]);

  // Remember what the resolver authorized for this identity, and forget it
  // the moment the resolver refuses.
  useEffect(() => {
    if (!storageKey || live === undefined) return;
    if (liveDenied) {
      forgetStorageUrl(storageKey);
    } else if (live.url) {
      writeStorageUrl(scope, storageKey, {
        url: live.url,
        urlExpiresAt: live.urlExpiresAt,
        validatedAt: Date.now(),
      });
    }
  }, [scope, storageKey, live, liveDenied]);

  const { refreshedEntry, refreshDenied } = useStorageUrlRefresh({
    scope,
    storageKey: shouldQuery ? storageKey : null,
    data: live,
    isAuthenticated: actorConfirmed,
    denied: liveDenied,
  });

  let resolvedUrl: string | null = null;
  if (isAlreadyUrl && typeof storageIdOrUrl === 'string') {
    resolvedUrl = storageIdOrUrl;
  } else if (shared.shareCacheKey && shared.url) {
    resolvedUrl = shared.url;
  } else if (legacyCacheKey) {
    resolvedUrl = legacyBlockedByShare
      ? null
      : liveLegacy !== undefined
        ? liveLegacy
        : (cachedLegacy?.url ?? null);
  } else if (
    storageKey &&
    actorConfirmed &&
    shared.memberAdmitted &&
    !refreshDenied
  ) {
    // Not echoed by the share: never fall through to member media.
    resolvedUrl = pickMemberStorageUrl({
      live,
      refreshed: refreshedEntry,
      cached: readStorageUrl(scope, storageKey),
    });
  }

  const isLoading = Boolean(
    !resolvedUrl &&
      storageIdOrUrl &&
      (shared.isLoading ||
        (legacyCacheKey !== null &&
          !legacyBlockedByShare &&
          isAuthenticated &&
          cachedLegacy === null &&
          liveLegacy === undefined) ||
        (storageKey !== null &&
          isAuthenticated &&
          shared.memberAdmitted &&
          !refreshDenied &&
          (live === undefined || live.refreshRequired)))
  );

  return {
    url: resolvedUrl,
    status: resolvedUrl ? 'ready' : isLoading ? 'loading' : 'unavailable',
  };
}

export function useStorageUrl(
  storageIdOrUrl: string | Id<'_storage'> | null | undefined,
  options?: {
    forceResolver?: boolean;
    brandingId?: string;
  }
): string | null {
  return useStorageUrlState(storageIdOrUrl, options).url;
}

/**
 * Hook to get the best available product image URL
 * Tries main asset first, then converted original image URL
 */
export function useProductImageUrl(
  product: { originalImageUrl?: string | null } | null | undefined,
  assetsData:
    | { assets?: { main?: Array<{ url?: string | null }> } }
    | null
    | undefined
): string | null {
  const originalImageUrl = useStorageUrl(product?.originalImageUrl);

  // Get main asset if available
  const mainAsset = assetsData?.assets?.main?.[0];

  return mainAsset?.url || originalImageUrl || null;
}
